import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Paper,
  Alert,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import PageHeader from '../../base/PageHeader';
import { BaseButton, BaseTextField } from '../../base';
import BaseModal from '../../base/BaseModal';
import RoleService, { Role } from '../../../../services/RoleService';

// ── Design tokens ─────────────────────────────────────────────────────────────
const FONT = '"Manrope", sans-serif';
const ACCENT = '#005e5d';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';
const BORDER = '#E6E7EB';
const SURFACE = '#F2F3F5';

const headerCellSx = {
  fontFamily: FONT, fontWeight: 700, fontSize: '0.75rem',
  letterSpacing: '0.05em', textTransform: 'uppercase' as const,
  color: TEXT_SECONDARY, backgroundColor: SURFACE,
  borderBottom: `1px solid ${BORDER}`, py: 1.5, px: 2,
};

const bodyCellSx = {
  fontFamily: FONT, fontSize: '0.875rem', color: TEXT_PRIMARY,
  borderBottom: `1px solid ${BORDER}`, py: 1.5, px: 2,
};

// ── Main component ─────────────────────────────────────────────────────────────
const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formName, setFormName] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formDescription, setFormDescription] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await RoleService.getAllRoles();
      setRoles(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const openCreate = () => {
    setEditingRole(null);
    setFormName('');
    setFormLabel('');
    setFormDescription('');
    setModalOpen(true);
  };

  const openEdit = (role: Role) => {
    setEditingRole(role);
    setFormName(role.name);
    setFormLabel(role.label);
    setFormDescription(role.description ?? '');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formLabel.trim()) { setError('Label is required'); return; }
    if (!editingRole && !formName.trim()) { setError('Name is required'); return; }

    try {
      setSaving(true);
      setError(null);
      if (editingRole) {
        await RoleService.updateRole(editingRole._id, { label: formLabel, description: formDescription });
        setSuccess(`Role "${editingRole.name}" updated`);
      } else {
        await RoleService.createRole({ name: formName, label: formLabel, description: formDescription });
        setSuccess(`Role "${formName}" created`);
      }
      setModalOpen(false);
      await fetchRoles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (role: Role) => {
    setDeleteTarget(role);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await RoleService.deleteRole(deleteTarget._id);
      setSuccess(`Role "${deleteTarget.name}" deleted`);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      await fetchRoles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Role Management"
        subtitle="Manage user roles. System roles cannot be deleted."
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Roles' },
        ]}
        actions={
          <BaseButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: '#004b4a' } }}
          >
            New Role
          </BaseButton>
        }
      />

      <Box sx={{ p: 3 }}>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.9375rem', color: TEXT_PRIMARY }}>
              All Roles ({roles.length})
            </Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchRoles} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} sx={{ color: ACCENT }} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Name</TableCell>
                    <TableCell sx={headerCellSx}>Label</TableCell>
                    <TableCell sx={headerCellSx}>Description</TableCell>
                    <TableCell sx={headerCellSx}>Type</TableCell>
                    <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role._id} hover>
                      <TableCell sx={bodyCellSx}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: ACCENT, fontWeight: 600 }}>
                          {role.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{role.label}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: TEXT_TERTIARY }}>
                        {role.description || '—'}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {role.isSystem ? (
                          <Chip
                            icon={<LockIcon sx={{ fontSize: '0.75rem !important' }} />}
                            label="System"
                            size="small"
                            sx={{ fontFamily: FONT, fontSize: '0.7rem', height: 22, backgroundColor: '#EDE7F6', color: '#4527A0' }}
                          />
                        ) : (
                          <Chip
                            label="Custom"
                            size="small"
                            sx={{ fontFamily: FONT, fontSize: '0.7rem', height: 22, backgroundColor: SURFACE, color: TEXT_SECONDARY }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                        <Tooltip title="Edit label / description">
                          <IconButton size="small" onClick={() => openEdit(role)}>
                            <EditIcon fontSize="small" sx={{ color: ACCENT }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={role.isSystem ? 'System roles cannot be deleted' : 'Delete'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={role.isSystem}
                              onClick={() => confirmDelete(role)}
                            >
                              <DeleteIcon fontSize="small" sx={{ color: role.isSystem ? TEXT_TERTIARY : '#C62828' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {roles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: TEXT_TERTIARY, fontFamily: FONT }}>
                        No roles found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Create / Edit Modal */}
      <BaseModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingRole ? `Edit Role: ${editingRole.name}` : 'Create New Role'}
        maxWidth="sm"
        actions={
          <>
            <BaseButton variant="outlined" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</BaseButton>
            <BaseButton variant="contained" onClick={handleSave} disabled={saving}
              sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: '#004b4a' } }}>
              {saving ? 'Saving…' : editingRole ? 'Save Changes' : 'Create Role'}
            </BaseButton>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {!editingRole && (
            <BaseTextField
              label="Role Name (slug)"
              value={formName}
              onChange={(e) => setFormName(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
              placeholder="e.g. content-editor"
              helperText="Lowercase, hyphens/underscores only. Cannot be changed later."
              fullWidth
            />
          )}
          <BaseTextField
            label="Display Label"
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            placeholder="e.g. Content Editor"
            fullWidth
          />
          <BaseTextField
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="What can this role do?"
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </BaseModal>

      {/* Delete Confirm Modal */}
      <BaseModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Role"
        maxWidth="xs"
        actions={
          <>
            <BaseButton variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={saving}>Cancel</BaseButton>
            <BaseButton variant="contained" onClick={handleDelete} disabled={saving}
              sx={{ backgroundColor: '#C62828', '&:hover': { backgroundColor: '#b71c1c' } }}>
              {saving ? 'Deleting…' : 'Delete'}
            </BaseButton>
          </>
        }
      >
        <Typography sx={{ fontFamily: FONT }}>
          Are you sure you want to delete role <strong>"{deleteTarget?.name}"</strong>?
          This action cannot be undone.
        </Typography>
      </BaseModal>
    </Box>
  );
};

export default RolesPage;
